// テーブルエディタ関連のハンドラー（設定項目を列、PCを行とする）

import { getSettings } from '../state/app-state.js';
import { getImportedApps } from '../state/app-state.js';
import { createAppSection } from '../components/app-importer.js';
import { getSettingTitle, getLocalizedText, t } from '../utils/i18n.js';
import { saveVariableValue } from '../state/app-state.js';
import { restoreSelectionStateLocal } from './storage-handlers.js';
import { restoreVariableValuesLocal } from './storage-handlers.js';

export interface TableColumn {
    type: 'pcname' | 'checkbox' | 'variable';
    itemId?: string; // 設定項目ID（checkbox/variableの場合）
    varName?: string; // 変数名（variableの場合）
    label: string; // 表示ラベル
}

export interface PCRow {
    id: string;
    pcName: string;
    checkboxes: Record<string, boolean>; // itemId -> checked
    variables: Record<string, Record<string, string>>; // itemId -> varName -> value
}

export interface TableData {
    columns: TableColumn[];
    rows: PCRow[];
}

const STORAGE_KEY_TABLE_DATA = 'winbatchgen_table_data_v2';
const STORAGE_KEY_CURRENT_PC = 'winbatchgen_current_pc_v1';

// 他モジュールからストレージキーを参照できるように公開
export const TABLE_DATA_STORAGE_KEY = STORAGE_KEY_TABLE_DATA;
export const CURRENT_PC_STORAGE_KEY = STORAGE_KEY_CURRENT_PC;

// 現在選択中のPC ID
let currentPCRowId: string | null = null;

/**
 * 現在選択中のPC IDを取得
 */
export function getCurrentPCRowId(): string | null {
    if (currentPCRowId === null && typeof window !== 'undefined' && window.localStorage) {
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY_CURRENT_PC);
            if (raw) {
                currentPCRowId = raw;
            }
        } catch (e) {
            console.warn('現在のPC IDの取得に失敗しました:', e);
        }
    }
    return currentPCRowId;
}

/**
 * 現在選択中のPC IDを設定
 */
export function setCurrentPCRowId(rowId: string | null): void {
    currentPCRowId = rowId;
    if (typeof window !== 'undefined' && window.localStorage) {
        try {
            if (rowId) {
                window.localStorage.setItem(STORAGE_KEY_CURRENT_PC, rowId);
            } else {
                window.localStorage.removeItem(STORAGE_KEY_CURRENT_PC);
            }
        } catch (e) {
            console.warn('現在のPC IDの保存に失敗しました:', e);
        }
    }
    updateCurrentPCIndicator();
}

/**
 * 現在選択中のPCの情報を取得
 */
export function getCurrentPC(): PCRow | null {
    const rowId = getCurrentPCRowId();
    if (!rowId) return null;
    
    const data = loadTableData();
    return data.rows.find(row => row.id === rowId) || null;
}

/**
 * 現在のPCのひとつ前のPCを選択
 */
export function selectPreviousPC(): void {
    const data = loadTableData();
    if (data.rows.length === 0) return;

    const currentId = getCurrentPCRowId();
    if (!currentId) {
        // まだ未選択なら先頭に移動
        const firstRow = data.rows[0];
        if (!firstRow) return;
        selectPC(firstRow.id);
        return;
    }

    const index = data.rows.findIndex(r => r.id === currentId);
    if (index === -1) {
        const firstRow = data.rows[0];
        if (!firstRow) return;
        selectPC(firstRow.id);
        return;
    }

    const prevIndex = (index - 1 + data.rows.length) % data.rows.length;
    const targetRow = data.rows[prevIndex];
    if (!targetRow) return;
    selectPC(targetRow.id);
}

/**
 * 現在のPCのひとつ次のPCを選択
 */
export function selectNextPC(): void {
    const data = loadTableData();
    if (data.rows.length === 0) return;

    const currentId = getCurrentPCRowId();
    if (!currentId) {
        // まだ未選択なら先頭に移動
        const firstRow = data.rows[0];
        if (!firstRow) return;
        selectPC(firstRow.id);
        return;
    }

    const index = data.rows.findIndex(r => r.id === currentId);
    if (index === -1) {
        const firstRow = data.rows[0];
        if (!firstRow) return;
        selectPC(firstRow.id);
        return;
    }

    const nextIndex = (index + 1) % data.rows.length;
    const targetRow = data.rows[nextIndex];
    if (!targetRow) return;
    selectPC(targetRow.id);
}

/**
 * 設定項目から列を生成
 */
function generateColumns(): TableColumn[] {
    const columns: TableColumn[] = [
        { type: 'pcname', label: t('tableEditorPcNamePlaceholder') }
    ];
    
    const settings = getSettings();
    if (!settings) return columns;
    
    const appSection = createAppSection(getImportedApps());
    const allSections = appSection && appSection.sections[0] 
        ? [appSection.sections[0], ...(settings.sections || [])] 
        : (settings.sections || []);
    
    allSections.forEach(section => {
        section.items.forEach(item => {
            // ヘッダーオプションやフッターオプションは除外
            if ((item as any).isHeaderOption || (item as any).isFooterOption || (item as any).isAdminCheck) {
                return;
            }
            
            // チェックボックス項目を追加
            columns.push({
                type: 'checkbox',
                itemId: item.id,
                label: getSettingTitle(item.id, item.title)
            });
            
            // 変数がある場合は変数ごとに列を追加
            if (item.variables && item.variables.length > 0) {
                item.variables.forEach(variable => {
                    columns.push({
                        type: 'variable',
                        itemId: item.id,
                        varName: variable.name,
                        label: `${getSettingTitle(item.id, item.title)} - ${getLocalizedText(variable.label)}`
                    });
                });
            }
        });
    });
    
    return columns;
}

/**
 * テーブルデータを保存
 */
export function saveTableData(data: TableData): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(STORAGE_KEY_TABLE_DATA, JSON.stringify(data));
    } catch (error) {
        console.error('テーブルデータの保存エラー:', error);
    }
}

/**
 * テーブルデータを復元
 */
export function loadTableData(): TableData {
    if (typeof window === 'undefined' || !window.localStorage) {
        return { columns: generateColumns(), rows: [] };
    }
    
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY_TABLE_DATA);
        if (!raw) {
            return { columns: generateColumns(), rows: [] };
        }
        const data = JSON.parse(raw) as TableData;
        // 列を再生成（設定項目が変更されている可能性があるため）
        data.columns = generateColumns();
        return data;
    } catch (error) {
        console.error('テーブルデータの読み込みエラー:', error);
        return { columns: generateColumns(), rows: [] };
    }
}

/**
 * 行を追加
 */
export function addRow(): void {
    const data = loadTableData();
    const newRow: PCRow = {
        id: `row${Date.now()}`,
        pcName: `PC${data.rows.length + 1}`,
        checkboxes: {},
        variables: {}
    };
    
    // デフォルト値を設定
    data.columns.forEach(col => {
        if (col.type === 'checkbox' && col.itemId) {
            newRow.checkboxes[col.itemId] = false;
        } else if (col.type === 'variable' && col.itemId && col.varName) {
            if (!newRow.variables[col.itemId]) {
                newRow.variables[col.itemId] = {};
            }
            const itemVars = newRow.variables[col.itemId];
            if (itemVars) {
                itemVars[col.varName] = '';
            }
        }
    });
    
    data.rows.push(newRow);
    saveTableData(data);
    renderTable();
}

/**
 * 行を削除
 */
export function removeRow(rowId: string): void {
    const data = loadTableData();
    data.rows = data.rows.filter(row => row.id !== rowId);
    saveTableData(data);
    
    // 削除されたPCが現在選択中のPCの場合は選択を解除
    if (getCurrentPCRowId() === rowId) {
        setCurrentPCRowId(null);
        restoreMainViewToDefault();
    }
    
    renderTable();
}

/**
 * PC名を更新
 */
export function updatePCName(rowId: string, pcName: string): void {
    const data = loadTableData();
    const row = data.rows.find(r => r.id === rowId);
    if (row) {
        row.pcName = pcName;
        saveTableData(data);
        updateCurrentPCIndicator();
    }
}

/**
 * チェックボックスの状態を更新
 */
export function updateCheckbox(rowId: string, itemId: string, checked: boolean): void {
    const data = loadTableData();
    const row = data.rows.find(r => r.id === rowId);
    if (row) {
        row.checkboxes[itemId] = checked;
        saveTableData(data);
        
        // 現在選択中のPCの場合はメイン画面も更新
        if (getCurrentPCRowId() === rowId) {
            const checkbox = document.getElementById(itemId) as HTMLInputElement | null;
            if (checkbox) {
                checkbox.checked = checked;
            }
        }
    }
}

/**
 * 変数の値を更新
 */
export function updateVariable(rowId: string, itemId: string, varName: string, value: string): void {
    const data = loadTableData();
    const row = data.rows.find(r => r.id === rowId);
    if (row) {
        if (!row.variables[itemId]) {
            row.variables[itemId] = {};
        }
        row.variables[itemId][varName] = value;
        saveTableData(data);
        
        // 現在選択中のPCの場合はメイン画面も更新
        if (getCurrentPCRowId() === rowId) {
            saveVariableValue(itemId, varName, value);
            const input = document.querySelector(`input[data-item-id="${itemId}"][data-var-name="${varName}"]`) as HTMLInputElement | null;
            if (input) {
                input.value = value;
            }
        }
    }
}

/**
 * PCを選択（メイン画面にそのPCの設定を反映）
 */
export function selectPC(rowId: string): void {
    const data = loadTableData();
    const row = data.rows.find(r => r.id === rowId);
    if (!row) return;
    
    setCurrentPCRowId(rowId);
    
    // メイン画面の設定をこのPCの設定に反映
    const settings = getSettings();
    if (!settings) return;
    
    const appSection = createAppSection(getImportedApps());
    const allSections = appSection && appSection.sections[0] 
        ? [appSection.sections[0], ...(settings.sections || [])] 
        : (settings.sections || []);
    
    // チェックボックスを更新
    allSections.forEach(section => {
        section.items.forEach(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            if (checkbox) {
                checkbox.checked = row.checkboxes[item.id] || false;
            }
        });
    });
    
    // 変数の値を更新
    Object.keys(row.variables).forEach(itemId => {
        const itemVars = row.variables[itemId];
        if (itemVars) {
            Object.keys(itemVars).forEach(varName => {
                const value = itemVars[varName];
                if (value !== undefined) {
                    saveVariableValue(itemId, varName, value);
                }
            });
        }
    });
    
    // 変数のUIを更新
    setTimeout(() => {
        restoreVariableValuesLocal();
    }, 100);
    
    // コードを更新
    if (window.app && window.app.updateCode) {
        window.app.updateCode(false);
    }
    
    // モーダルを閉じる
    closeTableEditor();
}

/**
 * メイン画面の設定を現在選択中のPCに保存
 */
export function saveCurrentPCFromMainView(): void {
    const rowId = getCurrentPCRowId();
    if (!rowId) return;
    
    const data = loadTableData();
    const row = data.rows.find(r => r.id === rowId);
    if (!row) return;
    
    const settings = getSettings();
    if (!settings) return;
    
    const appSection = createAppSection(getImportedApps());
    const allSections = appSection && appSection.sections[0] 
        ? [appSection.sections[0], ...(settings.sections || [])] 
        : (settings.sections || []);
    
    // チェックボックスの状態を保存
    allSections.forEach(section => {
        section.items.forEach(item => {
            const checkbox = document.getElementById(item.id) as HTMLInputElement | null;
            if (checkbox) {
                row.checkboxes[item.id] = checkbox.checked;
            }
        });
    });
    
    // 変数の値を保存
    if (typeof window !== 'undefined' && window.localStorage) {
        try {
            const raw = window.localStorage.getItem('winbatchgen_variables_v1');
            if (raw) {
                const variables: Record<string, Record<string, string>> = JSON.parse(raw);
                Object.keys(variables).forEach(itemId => {
                    if (!row.variables[itemId]) {
                        row.variables[itemId] = {};
                    }
                    const itemVars = variables[itemId];
                    if (itemVars && typeof itemVars === 'object') {
                        const rowItemVars = row.variables[itemId];
                        if (rowItemVars) {
                            Object.keys(itemVars).forEach(varName => {
                                const value = itemVars[varName];
                                if (value !== undefined && typeof value === 'string') {
                                    rowItemVars[varName] = value;
                                }
                            });
                        }
                    }
                });
            }
        } catch (e) {
            console.warn('変数の保存に失敗しました:', e);
        }
    }
    
    saveTableData(data);
}

/**
 * メイン画面をデフォルト状態に復元
 */
export function restoreMainViewToDefault(): void {
    restoreSelectionStateLocal();
    setTimeout(() => {
        restoreVariableValuesLocal();
        if (window.app && window.app.updateCode) {
            window.app.updateCode(false);
        }
    }, 100);
}

/**
 * 現在のPCインジケーターを更新
 */
export function updateCurrentPCIndicator(): void {
    const indicator = document.getElementById('currentPCIndicator');
    const pcNameElement = document.getElementById('currentPCName');
    
    const currentPC = getCurrentPC();
    
    if (indicator && pcNameElement) {
        if (currentPC) {
            pcNameElement.textContent = currentPC.pcName;
        } else {
            // PCが1台もない／未選択の場合でも、インジケーター自体は表示して
            // クリックでテーブルエディタを開けるようにしておく
            pcNameElement.textContent = '-';
        }
        indicator.classList.remove('hidden');
    }
}

/**
 * テーブルエディタを開く
 */
export function openTableEditor(): void {
    const modal = document.getElementById('tableEditorModal');
    const backdrop = document.getElementById('tableEditorBackdrop');
    
    if (modal && backdrop) {
        modal.classList.remove('hidden');
        backdrop.classList.remove('hidden');
        renderTable();
    }
}

/**
 * テーブルエディタを閉じる
 */
export function closeTableEditor(): void {
    const modal = document.getElementById('tableEditorModal');
    const backdrop = document.getElementById('tableEditorBackdrop');
    
    if (modal && backdrop) {
        modal.classList.add('hidden');
        backdrop.classList.add('hidden');
    }
}

/**
 * テーブルをレンダリング
 */
export function renderTable(): void {
    const container = document.getElementById('tableEditorContainer');
    if (!container) return;
    
    const data = loadTableData();
    const currentRowId = getCurrentPCRowId();

    // 設定定義を取得（行=設定／変数 用）
    const settings = getSettings();
    const appSection = createAppSection(getImportedApps());
    const allSections = settings
        ? (appSection && appSection.sections[0]
            ? [appSection.sections[0], ...(settings.sections || [])]
            : (settings.sections || []))
        : [];
    
    let html = `
        <div class="mb-2 text-xs text-gray-400 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p>${escapeHtml(t('tableEditorHelpText'))}</p>
            <div class="flex gap-2">
                <button onclick="window.app.addTableRow()" class="text-xs bg-blue-600 hover:bg-blue-500 px-3 py-1 rounded text-white transition">${escapeHtml(t('tableEditorAddPC'))}</button>
                <button onclick="window.app.clearTableData()" class="text-xs bg-gray-700 hover:bg-gray-600 px-3 py-1 rounded text-white transition">${escapeHtml(t('tableEditorClearTable'))}</button>
            </div>
        </div>
        <div class="overflow-x-auto mb-2">
            <table class="w-full border-collapse text-xs wb-table">
                <thead>
                    <tr class="bg-gray-700">
                        <th class="border border-gray-600 p-2 text-left sticky left-0 bg-gray-800 z-20 min-w-[220px]">
                            ${escapeHtml(t('tableEditorSettingVariableHeader'))}
                        </th>
    `;

    // 各PC列ヘッダー
    data.rows.forEach((pcRow, pcIndex) => {
        const isSelected = currentRowId === pcRow.id;
        html += `
            <th class="border border-gray-600 p-2 text-left min-w-[160px]">
                <div class="flex flex-col gap-1">
                    <input type="text"
                           value="${escapeHtml(pcRow.pcName)}"
                           onchange="window.app.updateTablePCName('${pcRow.id}', this.value)"
                           class="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-blue-500 wb-table-cell-input"
                           data-row-id="${pcRow.id}"
                           data-col-index="${pcIndex}"
                           placeholder="${escapeHtml(t('tableEditorPcNamePlaceholder'))}">
                    <div class="flex gap-1">
                        <button onclick="window.app.selectTablePC('${pcRow.id}')"
                                class="flex-1 text-[11px] px-2 py-1 rounded transition ${isSelected ? 'bg-purple-600 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}"
                                title="${escapeHtml(t('tableEditorSelectTitle'))}">
                            ${isSelected ? `✓ ${escapeHtml(t('tableEditorSelected'))}` : escapeHtml(t('tableEditorSelect'))}
                        </button>
                        <button onclick="window.app.removeTableRow('${pcRow.id}')"
                                class="text-[11px] px-2 py-1 rounded bg-red-600/80 hover:bg-red-500 text-white"
                                title="${escapeHtml(t('tableEditorDeleteTitle'))}">×</button>
                    </div>
                </div>
            </th>
        `;
    });

    html += `
                    </tr>
                    </thead>
                    <tbody>
    `;
    
    if (!settings || allSections.length === 0) {
        html += `
            <tr>
                <td colspan="${data.rows.length + 1}" class="border border-gray-600 p-4 text-center text-gray-500">
                    ${escapeHtml(t('tableEditorNoSettingsLoaded'))}
                </td>
            </tr>
        `;
    } else if (data.rows.length === 0) {
        html += `
            <tr>
                <td colspan="${data.rows.length + 1}" class="border border-gray-600 p-4 text-center text-gray-500">
                    ${escapeHtml(t('tableEditorNoPcs'))}
                </td>
            </tr>
        `;
    } else {
        // 行 = 設定（チェックボックス行 + 変数行）
        allSections.forEach(section => {
            section.items.forEach(item => {
                // ヘッダーオプションやフッターオプションは除外
                if ((item as any).isHeaderOption || (item as any).isFooterOption || (item as any).isAdminCheck) {
                    return;
                }

                const title = getSettingTitle(item.id, item.title);

                // 1行目：チェックボックス行
                html += `
                    <tr class="hover:bg-gray-800/40">
                        <td class="border border-gray-600 p-2 sticky left-0 bg-gray-900 z-10">
                            <span class="text-xs text-gray-100">${escapeHtml(title)}</span>
                        </td>
                `;

                data.rows.forEach((pcRow, pcIndex) => {
                    const checked = pcRow.checkboxes[item.id] || false;
                    html += `
                        <td class="border border-gray-600 p-2 text-center">
                            <input type="checkbox"
                                   ${checked ? 'checked' : ''}
                                   onchange="window.app.updateTableCheckbox('${pcRow.id}', '${item.id}', this.checked)"
                                   class="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 wb-table-cell-input"
                                   data-row-id="${pcRow.id}"
                                   data-col-index="${pcIndex}">
                        </td>
                    `;
                });

                html += `</tr>`;

                // 変数行（あれば）
                if (item.variables && item.variables.length > 0) {
                    item.variables.forEach(variable => {
                        const label = `${title} - ${getLocalizedText(variable.label)}`;
                        html += `
                            <tr class="hover:bg-gray-800/40">
                                <td class="border border-gray-600 p-2 sticky left-0 bg-gray-900 z-10">
                                    <span class="text-[11px] text-gray-300 pl-2">↳ ${escapeHtml(label)}</span>
                                </td>
                        `;

                        data.rows.forEach((pcRow, pcIndex) => {
                            const value = pcRow.variables[item.id]?.[variable.name] || '';
                            html += `
                                <td class="border border-gray-600 p-1">
                                    <input type="text"
                                           value="${escapeHtml(value)}"
                                           onchange="window.app.updateTableVariable('${pcRow.id}', '${item.id}', '${variable.name}', this.value)"
                                           class="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-white text-[11px] focus:outline-none focus:border-blue-500 wb-table-cell-input"
                                           data-row-id="${pcRow.id}"
                                           data-col-index="${pcIndex}"
                                           placeholder="${escapeHtml(t('tableEditorValuePlaceholder'))}">
                                </td>
                            `;
                        });

                        html += `</tr>`;
                    });
                }
            });
        });
    }
    
    html += `
                    </tbody>
                </table>
            </div>
    `;
    
    container.innerHTML = html;

    // Spreadsheet風のフォーカス・キーボード移動を設定
    setupTableInteractions(container);
}

/**
 * HTMLエスケープ
 */
function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * テーブルデータをクリア
 */
export function clearTableData(): void {
    if (confirm(t('tableEditorClearConfirm'))) {
        const data: TableData = { columns: generateColumns(), rows: [] };
        saveTableData(data);
        setCurrentPCRowId(null);
        restoreMainViewToDefault();
        renderTable();
        updateCurrentPCIndicator();
    }
}

/**
 * テーブル入力のフォーカス装飾とキーボード移動を設定
 */
function setupTableInteractions(container: HTMLElement): void {
    const inputs = Array.from(container.querySelectorAll<HTMLInputElement>('.wb-table-cell-input'));

    // フォーカス時にセルをハイライト
    inputs.forEach((el) => {
        el.addEventListener('focus', () => {
            clearFocusedCells(container);
            const td = el.closest('td');
            if (td) {
                td.classList.add('wb-table-cell-focused');
            }
        });
        el.addEventListener('blur', () => {
            const td = el.closest('td');
            if (td) {
                td.classList.remove('wb-table-cell-focused');
            }
        });

        el.addEventListener('keydown', (e) => {
            handleTableKeydown(e as KeyboardEvent, el, inputs);
        });
    });
}

function clearFocusedCells(container: HTMLElement): void {
    container.querySelectorAll('.wb-table-cell-focused').forEach((cell) => {
        cell.classList.remove('wb-table-cell-focused');
    });
}

function handleTableKeydown(e: KeyboardEvent, current: HTMLInputElement, allInputs: HTMLInputElement[]): void {
    const rowId = current.dataset.rowId;
    const colIndexStr = current.dataset.colIndex;
    const colIndex = colIndexStr ? parseInt(colIndexStr, 10) : NaN;

    // 無効な場合は何もしない
    if (!rowId || Number.isNaN(colIndex)) return;

    const isCheckbox = current.type === 'checkbox';

    // Enter/Tab/矢印キーのみカスタム動作
    const key = e.key;
    const isTab = key === 'Tab';
    const isEnter = key === 'Enter';
    const isArrow = key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight';

    if (!isTab && !isEnter && !isArrow) return;

    // テキスト入力中は、矢印キーはデフォルト動作を優先（キャレット移動）
    if (!isCheckbox && isArrow) {
        return;
    }

    e.preventDefault();

    // DOM順に並べた入力リスト
    const cells = allInputs;
    const currentIndex = cells.indexOf(current);
    if (currentIndex === -1) return;

    let target: HTMLInputElement | null = null;

    if (isTab || isEnter || key === 'ArrowRight') {
        // 右／次のセル
        const step = e.shiftKey && isTab ? -1 : 1;
        target = cells[currentIndex + step] || null;
    } else if (key === 'ArrowLeft') {
        target = cells[currentIndex - 1] || null;
    } else if (key === 'ArrowDown' || key === 'ArrowUp') {
        // 同じ列で上下移動
        const sameCol = cells.filter((el) => el.dataset.colIndex === colIndexStr);
        const idxInCol = sameCol.indexOf(current);
        if (idxInCol !== -1) {
            const offset = key === 'ArrowDown' ? 1 : -1;
            target = sameCol[idxInCol + offset] || null;
        }
    }

    if (target) {
        target.focus();
        if (target.type !== 'checkbox') {
            // テキスト入力は全選択してすぐ編集できるように
            target.select();
        }
    }
}
